// src/components/gestionEvento/RegistroFrom/RegistroManager.js

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';

import {
  AccordionDetails,
  AccordionSummary,
  Typography,
  Checkbox,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Box,
  FormGroup,
  FormControlLabel,
  Chip,
  Stack,
  Divider,
  Tooltip,
  Alert,
} from '@mui/material';

import {
  Save as SaveIcon,
  RestartAlt as ResetIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import CustomAccordion from '../../../CustomAccordion';
import { useCompetencia } from '../../../../context/CompetenciaContext';

/* =============================================================================
   Subcomponente: Fila de campo opcional (táctil, accesible y responsiva)
   - Toda la "tarjeta" se puede tocar para alternar el checkbox.
   - El TextField NO alterna el checkbox al escribir (se detiene la propagación).
   - Soporta teclado (Enter / Space).
============================================================================= */
function OptionalFieldRow({
  name,
  checked,
  labelValue,
  errorText,
  onToggle,
  onLabelChange,
}) {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const handleContainerClick = (e) => {
    const tag = (e.target.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'textarea') return;
    onToggle(name, !checked);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle(name, !checked);
    }
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-pressed={checked}
      onClick={handleContainerClick}
      onKeyDown={handleKeyDown}
      sx={{
        p: { xs: 1, sm: 1.25 },
        width: '100%',
        border: '1px solid',
        borderColor: checked ? 'primary.light' : 'divider',
        borderRadius: 1.5,
        bgcolor: checked ? 'action.selected' : 'background.paper',
        transition: 'background-color 0.2s, border-color 0.2s, box-shadow .2s',
        display: 'flex',
        alignItems: 'flex-start',
        gap: { xs: 1, sm: 1.25 },
        '&:hover': {
          borderColor: 'primary.main',
          bgcolor: checked ? 'action.selected' : 'action.hover',
          boxShadow: 1,
        },
        outline: 'none',
        '&:focus-visible': {
          boxShadow: `0 0 0 2px ${theme.palette.primary.main}55`,
        },
      }}
    >
      <Checkbox
        checked={checked}
        color="primary"
        size={isXs ? 'medium' : 'small'}
        sx={{ 
          mt: 0.5, 
          flexShrink: 0,
          p: 0,
          '& .MuiSvgIcon-root': {
            fontSize: isXs ? '24px' : '20px'
          }
        }}
        onChange={(e) => onToggle(name, e.target.checked)}
        inputProps={{ 'aria-label': `Activar campo ${name}` }}
      />

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <TextField
          fullWidth
          variant="standard"
          value={labelValue || ''}
          onChange={(e) => onLabelChange(name, e.target.value)}
          error={Boolean(errorText)}
          helperText={errorText}
          inputProps={{ 
            style: { 
              fontSize: isXs ? '16px' : '14px',
              paddingTop: 0,
              paddingBottom: 0
            } 
          }}
          InputProps={{
            disableUnderline: true,
            onClick: (e) => e.stopPropagation(),
            onFocus: (e) => e.stopPropagation(),
          }}
          sx={{
            '& .MuiInputBase-root': {
              padding: 0
            },
            '& .MuiFormHelperText-root': {
              mx: 0,
              mt: 0.5
            }
          }}
        />
      </Box>
    </Box>
  );
}

OptionalFieldRow.propTypes = {
  name: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  labelValue: PropTypes.string,
  errorText: PropTypes.string,
  onToggle: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func.isRequired,
};

/* =============================================================================
   Previsualización simple del formulario (solo muestra labels activos)
============================================================================= */
function RegistroPreview({ requiredLabels, optionalActive, optionalLabels }) {
  const activeOptional = Object.keys(optionalActive).filter((k) => optionalActive[k]);

  return (
    <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <InfoIcon fontSize="inherit" /> Vista previa rápida
      </Typography>
      <Grid container spacing={1.5} disableEqualOverflow>
        {['nombre', 'apellido', 'rama', 'fechaNacimiento', 'equipo', 'categoria', 'pruebas', 'email'].map((key) => (
          <Grid key={key} item xs={12} sm={6} md={6} lg={4}>
            <TextField fullWidth label={requiredLabels[key] || key} size="small" disabled />
          </Grid>
        ))}
        {activeOptional.map((key) => (
          <Grid key={key} item xs={12} sm={6} md={6} lg={4}>
            <TextField fullWidth label={optionalLabels[key] || key} size="small" disabled />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

RegistroPreview.propTypes = {
  requiredLabels: PropTypes.object.isRequired,
  optionalActive: PropTypes.object.isRequired,
  optionalLabels: PropTypes.object.isRequired,
};

/* =============================================================================
   Componente principal
============================================================================= */
const RegistroManager = ({ setSnackbar, competencia }) => {
  const {
    updateRegistrationConfig,
    loading: loadingCompetencias,
    defaultRegistroFields,
    defaultRegistroFieldLabels,
  } = useCompetencia();

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const beforeUnloadRef = useRef(false);

  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const [registroFields, setRegistroFields] = useState(defaultRegistroFields);
  const [registroFieldLabels, setRegistroFieldLabels] = useState(defaultRegistroFieldLabels);

  // =================== Cargar datos iniciales desde la competencia ===================
  useEffect(() => {
    if (competencia?.registration?.config) {
      const cfg = competencia.registration.config;
      setRegistroFields({ ...defaultRegistroFields, ...(cfg.registroFields || {}) });
      setRegistroFieldLabels({
        ...defaultRegistroFieldLabels,
        ...(cfg.registroFieldLabels || {}),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competencia]);

  // =================== Aviso de cambios sin guardar (cerrar pestaña) ===================
  useEffect(() => {
    const handler = (e) => {
      if (beforeUnloadRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  useEffect(() => {
    beforeUnloadRef.current = hasChanges;
  }, [hasChanges]);

  // =================== Validación: ningún label vacío ===================
  const validateFieldLabels = useCallback((labels) => {
    const errors = {};
    Object.entries(labels).forEach(([key, value]) => {
      if (!value || String(value).trim() === '') errors[key] = 'Este campo es requerido';
    });
    return errors;
  }, []);

  // =================== Handlers ===================
  const handleOptionalToggle = useCallback((name, nextChecked) => {
    setRegistroFields((prev) => ({ ...prev, [name]: nextChecked }));
    setHasChanges(true);
  }, []);

  const handleLabelChange = useCallback((name, value) => {
    setRegistroFieldLabels((prev) => ({ ...prev, [name]: value }));
    setHasChanges(true);

    setFieldErrors((prev) => {
      const newErrors = { ...prev };
      if (!value || String(value).trim() === '') newErrors[name] = 'Este campo es requerido';
      else delete newErrors[name];
      return newErrors;
    });
  }, []);

  const handleSaveChanges = async () => {
    if (!competencia?.id) {
      setSnackbar({
        open: true,
        message: 'No se puede guardar: competencia no válida',
        severity: 'error',
      });
      return;
    }

    const errors = validateFieldLabels(registroFieldLabels);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setSnackbar({
        open: true,
        message: 'Por favor completa todos los campos obligatorios',
        severity: 'error',
      });
      return;
    }

    setIsSaving(true);
    try {
      await updateRegistrationConfig(competencia.id, {
        registroFields,
        registroFieldLabels,
      });

      setSnackbar({
        open: true,
        message: 'Configuración de registro guardada correctamente',
        severity: 'success',
      });
      setHasChanges(false);
    } catch (error) {
      console.error('Error al guardar configuración:', error);
      setSnackbar({
        open: true,
        message: error?.message || 'Error al guardar la configuración',
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // (El botón de restaurar guardados se eliminó de la UI)

  const handleResetToDefaults = () => {
    setRegistroFields({ ...defaultRegistroFields });
    setRegistroFieldLabels({ ...defaultRegistroFieldLabels });
    setFieldErrors({});
    setHasChanges(true);
    setSnackbar({
      open: true,
      message: 'Se restablecieron los valores por defecto (recuerda guardar)',
      severity: 'warning',
    });
  };

  // =================== Estado de carga (no retornar antes de hooks) ===================
  const isLoading = loadingCompetencias || !competencia || !registroFields || !registroFieldLabels;

  // =================== Estilos de sección ===================
  const sectionSx = { '& .MuiOutlinedInput-root': { borderRadius: 2 } };

  // =================== Agrupación + orden de campos opcionales ===================
  const GROUPS = useMemo(
    () => ({
      personales: {
        title: 'Datos Personales',
        keys: ['telefono', 'fotoPerfil', 'contactoEmergencia', 'menorEdad'],
      },
      ubicacion: {
        title: 'Ubicación',
        keys: ['ciudad', 'estado', 'pais', 'direccion'],
      },
      equipo: {
        title: 'Equipo / Asociación',
        keys: ['logoEquipo', 'asociacion'],
      },
      otros: {
        title: 'Otros',
        keys: ['cuentaId'],
      },
    }),
    []
  );

  const ALL_OPTIONAL_ORDER = useMemo(
    () => [...GROUPS.personales.keys, ...GROUPS.ubicacion.keys, ...GROUPS.equipo.keys, ...GROUPS.otros.keys],
    [GROUPS]
  );

  // =================== Búsqueda / contador ===================
  const filterOptional = useCallback(() => true, []);

  // =================== Render ===================
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', height: '80vh', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Cargando ajustes de registro...</Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2} disableEqualOverflow sx={{ ...sectionSx, px: { xs: 0, sm: 1 } }}>
      {/* ======= Barra de acciones (pegajosa cuando hay cambios) ======= */}
      <Grid item xs={12}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{
            mb: 1.5,
            position: hasChanges ? 'sticky' : 'static',
            top: hasChanges ? 0 : 'auto',
            zIndex: 10,
            bgcolor: hasChanges ? 'background.paper' : 'transparent',
            pt: hasChanges ? 1 : 0,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button
              variant="contained"
              color="primary"
              startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveChanges}
              disabled={isSaving || Object.keys(fieldErrors).length > 0}
              sx={{ borderRadius: 2 }}
            >
              {isSaving ? 'Guardando…' : 'Guardar Cambios'}
            </Button>

            <Tooltip title="Volver a la configuración por defecto">
              <span>
                <Button
                  variant="text"
                  startIcon={<ResetIcon />}
                  onClick={handleResetToDefaults}
                  disabled={isSaving}
                  sx={{ borderRadius: 2 }}
                >
                  Restablecer por defecto
                </Button>
              </span>
            </Tooltip>

            {hasChanges && (
              <Chip
                icon={<CheckIcon />}
                color="warning"
                label="Cambios sin guardar"
                variant="outlined"
                sx={{ ml: 0.5 }}
              />
            )}
          </Stack>

          {/* Se removió el buscador para simplificar la UI */}
        </Stack>
      </Grid>

      {/* ==================== Campos obligatorios (labels) ==================== */}
      <Grid item xs={12}>
        <CustomAccordion title="Personalización de Campos Obligatorios" defaultExpanded showExpandIcon={false}>
          <AccordionDetails>
            <Grid container spacing={2} disableEqualOverflow>
              {[
                'nombre',
                'apellido',
                'rama',
                'fechaNacimiento',
                'equipo',
                'categoria',
                'pruebas',
                'email',
              ].map((key) => (
                <Grid key={key} item xs={12} sm={6} md={6} lg={4}>
                  <TextField
                    fullWidth
                    label={
                      {
                        nombre: 'Nombre(s)',
                        apellido: 'Apellido(s)',
                        rama: 'Rama',
                        fechaNacimiento: 'Fecha de Nacimiento',
                        equipo: 'Equipo',
                        categoria: 'Categoría',
                        pruebas: 'Pruebas',
                        email: 'Email',
                      }[key]
                    }
                    name={key}
                    value={registroFieldLabels[key] || ''}
                    onChange={(e) => handleLabelChange(key, e.target.value)}
                    error={!!fieldErrors[key]}
                    helperText={fieldErrors[key]}
                    variant="outlined"
                  />
                </Grid>
              ))}
            </Grid>

            {/* Previsualización rápida */}
            <Box sx={{ mt: 2 }}>
              <RegistroPreview
                requiredLabels={registroFieldLabels}
                optionalActive={registroFields}
                optionalLabels={registroFieldLabels}
              />
            </Box>
          </AccordionDetails>
        </CustomAccordion>
      </Grid>

      {/* =================== Campos opcionales (agrupados y responsivos) =================== */}
      <Grid item xs={12}>
        <CustomAccordion title="Mostrar/Ocultar Campos Opcionales" defaultExpanded showExpandIcon={false}>
          {/* Grupo: Datos personales */}
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={700}>{GROUPS.personales.title}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <FormGroup sx={{ width: '100%' }}>
<Grid container spacing={1.5} disableEqualOverflow sx={{ width: '100%', m: 0 }}>
  {GROUPS.personales.keys.filter(filterOptional).map((key) => (
    <Grid 
      key={key} 
      item 
      xs={12} 
      sm={6} 
      md={6} 
      lg={4}
      sx={{
        display: 'flex',
        '& > *': {
          width: '100%'
        }
      }}
    >
      <OptionalFieldRow
        name={key}
        checked={Boolean(registroFields[key])}
        labelValue={registroFieldLabels[key] || ''}
        errorText={fieldErrors[key]}
        onToggle={handleOptionalToggle}
        onLabelChange={handleLabelChange}
      />
    </Grid>
  ))}
</Grid>
            </FormGroup>
          </AccordionDetails>

          <Divider sx={{ my: 1.5 }} />

          {/* Grupo: Ubicación */}
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={700}>{GROUPS.ubicacion.title}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <FormGroup sx={{ width: '100%' }}>
              <Grid container spacing={1.5} disableEqualOverflow columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                {GROUPS.ubicacion.keys.filter(filterOptional).map((key) => (
    <Grid 
      key={key} 
      item 
      xs={12} 
      sm={6} 
      md={6} 
      lg={4}
      sx={{
        display: 'flex',
        '& > *': {
          width: '100%'
        }
      }}
    >
                    <OptionalFieldRow
                      name={key}
                      checked={Boolean(registroFields[key])}
                      labelValue={registroFieldLabels[key] || ''}
                      errorText={fieldErrors[key]}
                      onToggle={handleOptionalToggle}
                      onLabelChange={handleLabelChange}
                    />
                  </Grid>
                ))}
              </Grid>
            </FormGroup>
          </AccordionDetails>

          <Divider sx={{ my: 1.5 }} />

          {/* Grupo: Equipo/Asociación */}
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={700}>{GROUPS.equipo.title}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <FormGroup sx={{ width: '100%' }}>
              <Grid container spacing={1.5} disableEqualOverflow columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                {GROUPS.equipo.keys.filter(filterOptional).map((key) => (
    <Grid 
      key={key} 
      item 
      xs={12} 
      sm={6} 
      md={6} 
      lg={4}
      sx={{
        display: 'flex',
        '& > *': {
          width: '100%'
        }
      }}
    >
                    <OptionalFieldRow
                      name={key}
                      checked={Boolean(registroFields[key])}
                      labelValue={registroFieldLabels[key] || ''}
                      errorText={fieldErrors[key]}
                      onToggle={handleOptionalToggle}
                      onLabelChange={handleLabelChange}
                    />
                  </Grid>
                ))}
              </Grid>
            </FormGroup>
          </AccordionDetails>

          <Divider sx={{ my: 1.5 }} />

          {/* Grupo: Otros */}
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={700}>{GROUPS.otros.title}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <FormGroup sx={{ width: '100%' }}>
              <Grid container spacing={1.5} disableEqualOverflow columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                {GROUPS.otros.keys.filter(filterOptional).map((key) => (
                  <Grid key={key} item xs={12} sm={6} md={6} lg={4}>
                    <OptionalFieldRow
                      name={key}
                      checked={Boolean(registroFields[key])}
                      labelValue={registroFieldLabels[key] || ''}
                      errorText={fieldErrors[key]}
                      onToggle={handleOptionalToggle}
                      onLabelChange={handleLabelChange}
                    />
                  </Grid>
                ))}
              </Grid>
            </FormGroup>
          </AccordionDetails>

          {/* Sin buscador: siempre se muestran los campos de cada grupo */}
        </CustomAccordion>
      </Grid>
    </Grid>
  );
};

RegistroManager.propTypes = {
  setSnackbar: PropTypes.func.isRequired,
  competencia: PropTypes.object,
};

export default RegistroManager;
